import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

import fullLogo from "../assets/gs-logo-black-text.png";
import {
  LayoutDashboard,
  Users,
  Zap,
  Building2,
  Library,
  MessageSquare,
  Cpu,
  PanelLeftOpen,
  PanelRightOpen,
  ChevronDown,
  Calendar,
  LogOut,
  ChevronLeft,
  ChevronRight,
  LifeBuoy,
  Megaphone,
  BellIcon,
  Info,
  Tags,
  Search,
  Home,
  BuildingIcon,
  Building2Icon,
  Menu as MenuIcon,
} from "lucide-react";
import { DateTime } from "luxon";
import { useDashboardUI } from "../context/GlobalContext";
import api from "../helpers/axios";
import { validateUserRole, userSignOut, decodeAccessToken, formatRole } from "../helpers/helpers";
import { userRoles } from "../helpers/constant";
import userService from "../services/userService";
import { fetchUserProfile } from "../services/authService";

const getLastSevenDaysRange = () => ({
  start: DateTime.local().minus({ days: 7 }).startOf("day").toJSDate(),
  end: DateTime.local().endOf("day").toJSDate(),
});

const SidebarLayout = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [startDate, setStartDate] = useState(() => getLastSevenDaysRange().start);
  const [endDate, setEndDate] = useState(() => getLastSevenDaysRange().end);
  // Draft mirrors the calendar's in-progress pick (e.g. a start day clicked
  // but no end day yet) without touching the applied startDate/endDate above
  // — which drive the closed button's label and the API-triggering filters —
  // until a full range exists. Re-synced from the applied range every time
  // the popover opens or closes, so an abandoned partial pick never sticks.
  const [draftStartDate, setDraftStartDate] = useState(startDate);
  const [draftEndDate, setDraftEndDate] = useState(endDate);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const calendarRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const facilityDropdownRef = useRef(null);

  const {
    facilityList,
    setFacilityList,
    selectedFacilityFilter,
    setSelectedFacilityFilter,
    setFacilityUserList,
    setSelectedStartDateFilter,
    setSelectedEndDateFilter,
  } = useDashboardUI();

  const [isFacilityDropdownOpen, setIsFacilityDropdownOpen] = useState(false);
  const [facilitySearchQuery, setFacilitySearchQuery] = useState("");
  const user = decodeAccessToken();
  const [userProfile, setUserProfile] = useState(null);

  // Date picker can't go back further than the facility existed. For admins
  // (own facility only) this comes from the dedicated by-number fetch below;
  // for superadmins it's looked up from the already-fetched facilityList
  // whenever they switch the picker.
  const [facilityMinDate, setFacilityMinDate] = useState(null);

  // Outside click handlers
  useEffect(() => {
    function handle(e) {
      if (calendarOpen && calendarRef.current && !calendarRef.current.contains(e.target))
        setCalendarOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [calendarOpen]);

  // Re-sync the draft from the applied range on every open/close. Closing
  // mid-pick (e.g. only a start day clicked) discards that partial pick
  // instead of leaving it stuck as the displayed range; opening picks up
  // anything applied elsewhere (e.g. a facility switch) while it was closed.
  useEffect(() => {
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
  }, [calendarOpen]);

  useEffect(() => {
    function handle(e) {
      if (dropdownOpen && profileDropdownRef.current && !profileDropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [dropdownOpen]);

  useEffect(() => {
    function handle(e) {
      if (isFacilityDropdownOpen && facilityDropdownRef.current && !facilityDropdownRef.current.contains(e.target))
        setIsFacilityDropdownOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [isFacilityDropdownOpen]);

  // Clear the search box each time the facility picker closes, so it starts
  // fresh (rather than pre-filtered) the next time it's opened.
  useEffect(() => {
    if (!isFacilityDropdownOpen) setFacilitySearchQuery("");
  }, [isFacilityDropdownOpen]);

  useEffect(() => {
    if (!validateUserRole(userRoles.SUPERADMIN)) return;
    const facility = facilityList?.find(
      (f) => String(f.facility_number) === String(selectedFacilityFilter?.facility_number)
    );
    setFacilityMinDate(facility?.created_at ? new Date(facility.created_at) : null);
  }, [facilityList, selectedFacilityFilter]);

  const minYear = 2025;
  const maxYear = 2055;
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);

  const location = useLocation();
  const navigate = useNavigate();

  const routeTitles = {
    "/": "Dashboard",
    "/user-management": "User Management",
    "/prompt-management": "Prompt Management",
    "/facility": "Facility",
    "/org-collection": "Organization Collections",
    "/about-me/global": "About Page",
    "/feedback-management": "Feedback Management",
    "/campaigns": "Campaigns",
    "/support-tickets": "Support Tickets",
    "/assistants": "Assistants",
    "/chat-categories": "Chat Categories",
    "/notifications": "Notifications",
  };
  const pageTitle = routeTitles[location.pathname]
    || (location.pathname.match(/^\/facility\/[^/]+$/) ? "Facility Detail" : "")
    || (location.pathname.startsWith("/collections/") ? "Collection Files" : "")
    || (location.pathname.startsWith("/campaigns/") ? "Campaign Detail" : "")
    || (location.pathname.startsWith("/notifications/") ? "Notification Detail" : "")
    || (location.pathname === "/create-assistant" ? "Create Assistant" : "")
    || (location.pathname.startsWith("/update-assistant-files/") ? "Update Assistant Files" : "")
    || (location.pathname.startsWith("/update-assistant/") ? "Update Assistant" : "")
    || (location.pathname.startsWith("/users/") ? "User Details" : "");

  useEffect(() => {
    fetchUserDetails();
    fetchFacilityList();
    fetchFacilityUserList();
  }, []);

  const handleLogout = async () => {
    await userSignOut();
  };

  const fetchFacilityList = async () => {
    try {
      // Admins are scoped to their own facility, so fetch just that one
      // (for its created_at, to floor the date picker) instead of the full
      // facility list superadmins get for the picker.
      if (!validateUserRole(userRoles.SUPERADMIN)) {
        if (!user?.facility_id) return;
        const res = await api.get(`/facility/by-number/${user.facility_id}`);
        setFacilityMinDate(res.data?.created_at ? new Date(res.data.created_at) : null);
        return;
      }
      const res = await api.get("/facility/");
      const sorted = [...res.data].sort((a, b) => a.name.localeCompare(b.name));
      setFacilityList(sorted);
    } catch (error) {
      console.error("Error fetching facility list", error);
    }
  };

  const fetchFacilityUserList = async () => {
    try {
      const res = await userService.getAllUsers();
      if (res) setFacilityUserList(res);
    } catch (error) {
      setFacilityUserList([]);
      console.error("Error fetching facility user list", error);
    }
  };

  const fetchUserDetails = async () => {
    try {
      const res = await fetchUserProfile();
      if (res) setUserProfile(res);
    } catch (error) {
      setUserProfile(null);
      console.error("Error fetching user profile:", error);
    }
  };

  // Applies a complete range: updates the button label, the draft the
  // calendar renders, and the context filters that actually trigger fetches.
  const commitRange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setDraftStartDate(start);
    setDraftEndDate(end);
    setSelectedStartDateFilter(start);
    setSelectedEndDateFilter(end);
    window.dispatchEvent(new CustomEvent("dateRangeChanged", { detail: { start, end } }));
  };

  const applyFacilityFilter = (facility) => {
    setSelectedFacilityFilter(facility);
    setIsFacilityDropdownOpen(false);

    // The previously selected range may predate this facility (or its new
    // minDate floor), so reset to the default "Last 7 days" rather than
    // leaving a stale, possibly-invalid range selected.
    const { start, end } = getLastSevenDaysRange();
    commitRange(start, end);
  };

  const checkDomain = () =>
    !location.pathname.includes("/facility") &&
    !location.pathname.includes("/addFacility") &&
    !location.pathname.includes("/editFacility") &&
    !location.pathname.includes("/create-assistant") &&
    !location.pathname.includes("/update-assistant/") &&
    !location.pathname.includes("/update-assistant-files/");

  const userInitials = userProfile?.display_name
    ? userProfile.display_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : userProfile?.email
    ? userProfile.email[0].toUpperCase()
    : "U";

  // Build nav items dynamically
  const navItems = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/user-management", label: "User Management", icon: Users },
    { to: "/prompt-management", label: "Prompt Management", icon: Zap },
    ...(validateUserRole(userRoles.SUPERADMIN)
      ? [
          { to: "/facility", label: "Facility", icon: Building2 },
          { to: "/org-collection", label: "Organization Collection", icon: Library },
          { to: "/about-me/global", label: "About Page", icon: Info },
        ]
      : []),
    { to: "/feedback-management", label: "Feedback Management", icon: MessageSquare },
    ...(validateUserRole(userRoles.SUPERADMIN)
      ? [{ to: "/assistants", label: "Assistants", icon: Cpu }]
      : []),
    ...(validateUserRole(userRoles.SUPERADMIN)
      ? [{ to: "/chat-categories", label: "Chat Categories", icon: Tags }]
      : []),
     ...(validateUserRole(userRoles.SUPERADMIN) ?
    [{ to: "/campaigns", label: "Campaigns", icon: Megaphone }] : []),
    ...(validateUserRole(userRoles.SUPERADMIN)
      ? [{ to: "/notifications", label: "Notifications", icon: BellIcon }]
      : []),
    { to: "/support-tickets", label: "Support Tickets", icon: LifeBuoy }
  ];

  const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const formatDateLabel = (d) =>
  d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });

const formatRangeLabel = (start, end) => {
  if (!start) return "Select range";

  // If only one date is selected (end is null) OR start/end are the same day -> show single date
  if (!end || isSameDay(start, end)) {
    return formatDateLabel(start);
  }

  // Otherwise show a range. Drop the start year only when it matches the
  // end year — a range spanning years must show both, or the start looks
  // like it's in the end year too.
  const sameYear = start.getFullYear() === end.getFullYear();
  const startText = start.toLocaleDateString(
    "en-US",
    sameYear ? { month: "short", day: "2-digit" } : { month: "short", day: "2-digit", year: "numeric" }
  );
  const endText = end.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  return `${startText} – ${endText}`;
};

  // Responsive shell: on laptops/desktops (>=1024px) the sidebar is docked (and can
  // collapse to an icon rail). On tablets/phones it becomes a slide-out menu
  // opened from the header's menu button, so the content gets the full width.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window === "undefined" || window.innerWidth >= 1024
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => {
      setIsDesktop(e.matches);
      if (e.matches) setMobileNavOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Close the slide-out menu after navigating
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const isCollapsed = collapsed && isDesktop;

  return (
    <div className="flex h-screen bg-white">

      {/* Backdrop for the slide-out menu (tablet / phone) */}
      {!isDesktop && mobileNavOpen && (
        <div
          className="fixed inset-0 z-[70] bg-[#14122B]/40"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* ── Sidebar — fixed drawer ── */}
      <div
        className={`${
          isCollapsed ? "w-[72px] min-w-[72px]" : "w-72 min-w-72"
        } flex-shrink-0 bg-white border-r border-gray-100 transition-all duration-300 overflow-y-auto flex flex-col ${
          isDesktop
            ? ""
            : `fixed inset-y-0 left-0 z-[80] max-w-[85vw] shadow-2xl transition-transform ${
                mobileNavOpen ? "translate-x-0" : "-translate-x-full"
              }`
        }`}
      >
        <div
          onClick={() => (isDesktop ? setCollapsed(!collapsed) : setMobileNavOpen(false))}
          className={`flex items-center h-20 flex-shrink-0 cursor-pointer group px-4 ${
            isCollapsed ? "flex-col justify-center gap-1.5" : "justify-between border-b border-gray-100"
          }`}
        >
          {isCollapsed ? (
            <>
              <img
                src={`${process.env.PUBLIC_URL}/favicon.png`}
                alt="Genserve"
                className="h-9 w-9 rounded-full object-cover"
              />
              <PanelLeftOpen
                aria-label="Expand sidebar"
                className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition"
              />
            </>
          ) : (
            <>
              <div className="flex-1 flex items-center justify-start">
                <img src={fullLogo} alt="Genserve Logo" className="h-12 block" />
              </div>
              <PanelRightOpen className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition flex-shrink-0" />
            </>
          )}
        </div>

        <nav className="mt-4 flex-1 px-3 space-y-1">
          {navItems.map(({ to, label, icon: Icon }) => {
            const active = location.pathname === to
              || (to === "/assistants" && (
                location.pathname === "/create-assistant"
                || location.pathname.startsWith("/update-assistant/")
                || location.pathname.startsWith("/update-assistant-files/")
              ))
              || (to === "/org-collection" && location.pathname.startsWith("/collections"));
            return (
              <Link
                key={to}
                to={to}
                title={isCollapsed ? label : undefined}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                  active
                    ? "bg-[#EEF0FB] text-[#2B3674]"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                } ${isCollapsed ? "justify-center" : ""}`}
              >
                <Icon
                  className={`flex-shrink-0 w-[18px] h-[18px] ${
                    active ? "text-[#2B3674]" : "text-gray-400"
                  }`}
                />
                {!isCollapsed && <span>{label}</span>}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* ── Main area ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* ── Header ── */}
        <div className="flex-shrink-0 z-50">
          <div className="bg-white border-b border-gray-100 shadow-sm flex flex-wrap lg:flex-nowrap items-center px-3 sm:px-6 py-3 min-h-20 gap-y-2 lg:gap-y-0 gap-x-2 sm:gap-x-4">

            {/* Round logo (tablet / phone) */}
            {!isDesktop && (
              <img
                src={`${process.env.PUBLIC_URL}/favicon.png`}
                alt="Genserve"
                className="h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0 rounded-full object-cover"
              />
            )}

            {/* Menu button (tablet / phone) */}
            {!isDesktop && (
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open menu"
                className="flex h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-xl border border-[#ECEEF4] bg-white text-gray-600 hover:bg-gray-50"
              >
                <MenuIcon className="h-5 w-5" />
              </button>
            )}

            {/* Page title */}
            <div className="order-last flex min-h-[44px] w-full items-center gap-2 min-w-0 lg:order-none lg:w-auto lg:flex-1">
              <span className="w-1 self-stretch rounded-sm bg-[#2B3674] flex-shrink-0" />
              <div className="flex flex-col justify-center min-w-0 gap-0.5">
                <div className="flex items-center gap-2 min-w-0">
                  <h1 className="text-xl font-bold text-gray-900 tracking-tight truncate">
                    {pageTitle}
                  </h1>
                  {location.pathname === "/" && (
                    <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-[#E7F7EE] px-2.5 py-0.5 text-xs font-semibold text-[#12945A]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#12945A]/60" />
                      Live
                    </span>
                  )}
                </div>
                {location.pathname === "/" && (
                  <p className="text-xs text-gray-400 truncate">
                    Platform overview across all connected facilities
                  </p>
                )}
                {location.pathname === "/prompt-management" && (
                  <p className="text-xs text-gray-400 truncate">
                    Author, share and publish prompts across facilities
                  </p>
                )}
                {location.pathname === "/feedback-management" && (
                  <p className="text-xs text-gray-400 truncate">
                    Monitor AI performance, triage feedback and manage incidents
                  </p>
                )}
                {location.pathname === "/user-management" && (
                  <p className="text-xs text-gray-400 truncate">
                    Manage members, roles and access across your organization
                  </p>
                )}
                {location.pathname === "/support-tickets" && (
                  <p className="text-xs text-gray-400 truncate">
                    Track, triage and resolve customer support requests
                  </p>
                )}
                {location.pathname === "/facility" && (
                  <p className="text-xs text-gray-400 truncate">
                    Manage facilities, domains and per-facility limits
                  </p>
                )}
              </div>
            </div>

            <div className="ml-auto flex min-w-0 flex-1 items-center justify-end gap-1.5 lg:ml-0 lg:flex-none lg:flex-shrink-0 sm:gap-2.5">

              {/* Facility picker */}
              {validateUserRole(userRoles.SUPERADMIN) && checkDomain() && (
                <div className="relative min-w-[64px]" ref={facilityDropdownRef}>
                  <button
                    onClick={() => setIsFacilityDropdownOpen(!isFacilityDropdownOpen)}
                    className={`flex w-full items-center gap-1.5 sm:gap-2 h-10 px-2.5 sm:px-3 md:px-4 rounded-full text-sm font-medium border transition-all ${
                      isFacilityDropdownOpen
                        ? "bg-white text-gray-800 border-gray-300"
                        : "bg-white text-gray-800 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Building2Icon className="w-4 h-4 flex-shrink-0 text-violet-600" strokeWidth={2.5} />
                    <span className="min-w-0 max-w-[84px] sm:max-w-[110px] md:max-w-[150px] truncate">
                      {facilityList?.find(
                        (f) => String(f.facility_number) === String(selectedFacilityFilter?.facility_number)
                      )?.name || "Select Facility"}
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${isFacilityDropdownOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isFacilityDropdownOpen && (
                    <div className="fixed inset-x-3 top-24 z-[90] w-auto sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:z-50 sm:mt-2 sm:w-80 max-h-80 overflow-y-auto scrollbar-gray bg-white border border-gray-100 rounded-2xl shadow-lg">
                      <div className="px-4 pt-4 pb-2">
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            autoFocus
                            value={facilitySearchQuery}
                            onChange={(e) => setFacilitySearchQuery(e.target.value)}
                            placeholder="Search facility..."
                            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-full bg-gray-100 border border-transparent placeholder:text-gray-500 focus:outline-none"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between px-4 pt-1 pb-2">
                        <span className="text-sm font-semibold text-gray-700">All facilities</span>
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs font-medium text-gray-500">
                          {facilityList?.length || 0}
                        </span>
                      </div>

                      <ul className="pb-2 px-2">
                        {facilityList
                          ?.filter((facility) =>
                            facility.name.toLowerCase().includes(facilitySearchQuery.trim().toLowerCase())
                          )
                          .map((facility) => (
                            <li key={facility.id}>
                              <Link
                                className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-normal text-gray-700 hover:bg-gray-100 transition-colors"
                                onClick={() => applyFacilityFilter(facility)}
                              >
                                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 text-xs font-medium flex-shrink-0">
                                  {facility.name?.trim().charAt(0).toUpperCase()}
                                </span>
                                {facility.name}
                              </Link>
                            </li>
                          ))}
                        {facilityList?.length > 0 &&
                          facilityList.filter((facility) =>
                            facility.name.toLowerCase().includes(facilitySearchQuery.trim().toLowerCase())
                          ).length === 0 && (
                            <li className="px-2 py-2.5 text-sm text-gray-400">No facilities found</li>
                          )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Date range — dashboard and prompt management */}
              {(location.pathname === "/") && (
                <div ref={calendarRef} className="relative flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCalendarOpen(!calendarOpen);
                    }}
                    className={`flex items-center gap-2 h-10 px-2 sm:px-3 md:px-4 rounded-full text-sm font-medium border transition-all ${
                      calendarOpen
                        ? "bg-gray-50 text-gray-800 border-gray-300"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <Calendar className="w-4 h-4 flex-shrink-0 text-violet-600" strokeWidth={2.5} />
                    <span className="hidden md:inline">
                      {draftStartDate && !draftEndDate
                        ? `${formatDateLabel(draftStartDate)} – Select end date`
                        : formatRangeLabel(startDate, endDate)}
                    </span>
                  </button>

                  {calendarOpen && (
                    <div className="fixed inset-x-3 top-24 z-[90] flex justify-center sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:z-50 sm:mt-2 sm:block">
                      <MiniCalendar
                        startDate={draftStartDate}
                        endDate={draftEndDate}
                        minDate={facilityMinDate}
                        onChange={(s, e) => {
                          // Draft always reflects the in-progress pick, so the
                          // calendar grid and the button label above can show
                          // a start-only selection. The applied range (and the
                          // filters that trigger API calls) only commit once a
                          // full range exists — not on the first (start-only)
                          // click — so no fetch fires for a half-finished pick.
                          setDraftStartDate(s);
                          setDraftEndDate(e);
                          if (s && e) commitRange(s, e);
                        }}
                        onClose={() => setCalendarOpen(false)}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Profile button */}
              <div className="relative flex-shrink-0" ref={profileDropdownRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(!dropdownOpen);
                  }}
                  className="flex flex-shrink-0 items-center gap-1 sm:gap-1.5 h-10 pl-0 sm:pl-1 pr-2 sm:pr-3 rounded-full hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#2B3674] text-white text-sm font-semibold flex-shrink-0">
                    {userInitials}
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {dropdownOpen && (
                  <div className="fixed inset-x-3 top-24 z-[90] w-auto max-h-[calc(100vh-7rem)] overflow-y-auto sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:z-50 sm:mt-2 sm:w-72 sm:max-h-none sm:overflow-hidden bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden">
                    <div className="p-2.5">
                      <div className="flex items-center gap-3 px-3.5 py-3.5 rounded-xl bg-gray-50">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#2B3674] text-white font-bold text-sm shadow-sm flex-shrink-0">
                          {userInitials}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-bold text-gray-900 truncate">
                            {userProfile?.display_name || userProfile?.username}
                          </span>
                          <span className="text-sm font-medium text-gray-700 truncate">
                            {userProfile?.email}
                          </span>
                          <span className="text-xs text-gray-400 mt-0.5">
                            {formatRole(userProfile?.role)}
                            {userProfile?.facility?.name ? ` · ${userProfile.facility.name}` : ""}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-2.5">
                      <button
                        onClick={handleLogout}
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 text-sm font-semibold transition"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        <main className={`flex-1 px-3 sm:px-5 pb-5 ${location.pathname === "/user-management" ? "overflow-y-auto lg:overflow-hidden" : "overflow-y-auto"} scrollbar-gray ${location.pathname === "/" ? "bg-[#F7F7FB]" : (location.pathname === "/prompt-management" || location.pathname === "/feedback-management" || location.pathname === "/facility" || location.pathname === "/user-management" || location.pathname === "/support-tickets" || location.pathname === "/org-collection" || location.pathname === "/assistants" || location.pathname === "/campaigns" || location.pathname === "/notifications" || location.pathname === "/chat-categories" || location.pathname.startsWith("/update-assistant-files/") || (location.pathname.startsWith("/collections/") && !location.pathname.includes("/files/"))) ? "bg-[#F7F7FB]" : "bg-white"}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default SidebarLayout;

// ── Mini Calendar ──
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const DAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// `minDate` floors selectable dates at the facility's created_at (resolved by
// the caller). It's never taken as later than 30 days ago, though — the
// "Last 30 days" preset below must always stay selectable, even for a
// facility created a few days ago.
const MiniCalendar = ({ startDate, endDate, onChange, minDate, onClose }) => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  thirtyDaysAgo.setHours(0, 0, 0, 0);
  const facilityFloor = minDate ? new Date(minDate) : null;
  if (facilityFloor) facilityFloor.setHours(0, 0, 0, 0);
  const normalizedMinDate =
    facilityFloor && facilityFloor < thirtyDaysAgo ? facilityFloor : thirtyDaysAgo;

  const initialRangeRef = useRef({ startDate, endDate });

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [hoverDate, setHoverDate] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [pickerView, setPickerView] = useState("days"); // "days" | "months"
  const [pickerYear, setPickerYear] = useState(today.getFullYear());
  const [activePreset, setActivePreset] = useState("Custom range");

  const presets = [
    { label: "Today", days: 0 },
    { label: "Last 7 days", days: 7 },
    { label: "Last 14 days", days: 14 },
    { label: "Last 30 days", days: 30 },
    { label: "This quarter", quarter: true },
    { label: "Custom range", custom: true },
  ];

  const applyPreset = (preset) => {
    setActivePreset(preset.label);
    if (preset.custom) {
      setSelecting(false);
      return;
    }
    const end = new Date(); end.setHours(23,59,59,999);
    let start;
    if (preset.quarter) {
      const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
      start = new Date(today.getFullYear(), quarterStartMonth, 1);
    } else {
      start = new Date(); start.setDate(start.getDate() - preset.days);
    }
    start.setHours(0, 0, 0, 0);
    onChange(start, end);
    setSelecting(false);
    setPickerView("days");
    setViewMonth(today.getMonth());
    setViewYear(today.getFullYear());
  };

  const handleCancel = () => {
    const { startDate: initStart, endDate: initEnd } = initialRangeRef.current;
    onChange(initStart, initEnd);
    onClose?.();
  };

  const handleApply = () => {
    onClose?.();
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    const n = new Date(viewYear, viewMonth + 1, 1);
    if (n <= today) {
      if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
      else setViewMonth(m => m + 1);
    }
  };
  const isNextDisabled = () => new Date(viewYear, viewMonth + 1, 1) > today;

  const selectMonth = (monthIdx) => {
    setViewMonth(monthIdx);
    setViewYear(pickerYear);
    setPickerView("days");
  };

  const isMonthDisabled = (monthIdx) => {
    // disable months in the future
    return new Date(pickerYear, monthIdx, 1) > today;
  };

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

  const normalize = (d) => { if (!d) return null; const c = new Date(d); c.setHours(0,0,0,0); return c; };

  const getDayState = (day) => {
    if (!day) return "empty";
    const d = normalize(day);
    const s = normalize(startDate);
    const e = normalize(endDate);
    const h = normalize(hoverDate);
    const todayN = normalize(new Date());
    if (d > todayN || (normalizedMinDate && d < normalizedMinDate)) return "disabled";
    const rangeEnd = selecting ? h : e;
    if (s && d.getTime() === s.getTime() && (!rangeEnd || d.getTime() === normalize(rangeEnd)?.getTime())) return "single";
    if (s && d.getTime() === s.getTime()) return "start";
    if (rangeEnd && d.getTime() === normalize(rangeEnd)?.getTime()) return "end";
    if (s && rangeEnd && d > s && d < normalize(rangeEnd)) return "inRange";
    return "default";
  };

  const handleDayClick = (day) => {
    const d = normalize(day);
    const todayN = normalize(new Date());
    if (!d || d > todayN || (normalizedMinDate && d < normalizedMinDate)) return;
    setActivePreset("Custom range");
    if (!selecting) {
      onChange(d, null);
      setSelecting(true);
    } else {
      const s = normalize(startDate);
      if (d < s) { onChange(d, s); } else { onChange(s, d); }
      setSelecting(false);
    }
  };

  const dayStyles = {
    empty: "invisible",
    disabled: "text-gray-200 cursor-default",
    default: "text-gray-700 hover:bg-indigo-50 hover:text-indigo-900 cursor-pointer rounded-md",
    single: "bg-blue-500 text-white font-semibold rounded-md cursor-pointer",
    start: "bg-blue-500 text-white font-semibold rounded-md cursor-pointer",
    end: "bg-blue-500 text-white font-semibold rounded-md cursor-pointer",
    inRange: "bg-violet-100 text-violet-800 cursor-pointer rounded-md",
  };

  return (
    <div className="flex flex-col bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden max-h-[80vh] overflow-y-auto w-full sm:w-auto">
      <div className="flex flex-col sm:flex-row">
      {/* Presets side — plain text list (a wrapping row of chips on phones) */}
      <div className="flex flex-row flex-wrap gap-1 sm:flex-col sm:gap-0 py-2 sm:py-3 px-2 border-b sm:border-b-0 sm:border-r border-gray-100 w-full sm:w-[161px] flex-shrink-0">
        {presets.map((preset) => {
          const isActive = activePreset === preset.label;
          return (
            <button
              key={preset.label}
              onClick={() => applyPreset(preset)}
              className={`sm:w-full text-left px-3 py-2 text-[13px] rounded-lg transition-colors ${
                isActive
                  ? "bg-indigo-50 text-indigo-900 font-medium"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      {/* Calendar side */}
      <div className="p-3 w-full max-w-[269px] sm:w-[269px] flex-shrink-0 mx-auto">

        {pickerView === "days" ? (
          <>
            {/* Day view header */}
            <div className="flex items-center justify-between mb-2">
              <button onClick={prevMonth} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => { setPickerYear(viewYear); setPickerView("months"); }}
                className="text-xs font-semibold text-gray-700 hover:text-indigo-900 hover:bg-indigo-50 px-2 py-1 rounded-md transition"
              >
                {MONTHS[viewMonth]} {viewYear}
              </button>
              <button onClick={nextMonth} disabled={isNextDisabled()} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition disabled:opacity-20 disabled:cursor-default">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Day labels */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DAYS.map(d => (
                <div key={d} className="text-center text-[10px] font-semibold text-gray-400 w-8 h-8 flex items-center justify-center">{d}</div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 gap-1">
              {cells.map((day, i) => {
                const state = getDayState(day);
                return (
                  <div
                    key={i}
                    className={`w-8 h-8 flex items-center justify-center text-xs transition-colors ${dayStyles[state]}`}
                    onClick={() => day && handleDayClick(day)}
                    onMouseEnter={() => selecting && day && setHoverDate(day)}
                    onMouseLeave={() => setHoverDate(null)}
                  >
                    {day ? day.getDate() : ""}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Month picker view */}
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => setPickerYear(y => y - 1)} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs font-semibold text-gray-700">{pickerYear}</span>
              <button
                onClick={() => setPickerYear(y => y + 1)}
                disabled={pickerYear >= today.getFullYear()}
                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition disabled:opacity-20 disabled:cursor-default"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Month grid */}
            <div className="grid grid-cols-3 gap-1">
              {MONTHS.map((m, i) => {
                const isActive = i === viewMonth && pickerYear === viewYear;
                const disabled = isMonthDisabled(i);
                return (
                  <button
                    key={m}
                    onClick={() => !disabled && selectMonth(i)}
                    disabled={disabled}
                    className={`py-1.5 text-xs rounded-lg font-medium transition ${
                      isActive
                        ? "bg-indigo-600 text-white"
                        : disabled
                        ? "text-gray-200 cursor-default"
                        : "text-gray-600 hover:bg-indigo-50 hover:text-indigo-900"
                    }`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>

            {/* Back button */}
            <button
              onClick={() => setPickerView("days")}
              className="mt-3 w-full text-[10px] text-gray-400 hover:text-gray-600 transition text-center"
            >
              ← Back to calendar
            </button>
          </>
        )}

        {/* Bottom action bar — aligned under the calendar column only */}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-3 py-2 rounded-lg text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 transition"
          >
            Apply
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};